import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Info, X, Filter, ArrowUpDown, Zap, ChevronDown, ChevronRight } from 'lucide-react';
import { API_BASE } from '@/env';
import { apiFetch } from '@/auth/apiClient';

// ─── Types matching backend ScannerPanelSummary ─────────────────────

interface FilterSummaryItem {
  field: string;
  operator: string;
  value: string;
  description: string;
}

interface SortSummaryItem {
  field: string;
  direction: string;
}

interface StrategySummaryItem {
  id: string;
  name: string;
  description: string;
  category: string;
  alertTypes: string[];
  alertLogic: string;
  filters: FilterSummaryItem[];
  filterLogic: string;
  enabled: boolean;
  color: string;
}

interface ScannerPanelSummary {
  scannerId: string;
  name: string;
  description: string;
  type: string;
  filters: FilterSummaryItem[];
  sort: SortSummaryItem | null;
  maxResults: number;
  strategies: StrategySummaryItem[];
}

// ─── Small sub-components ───────────────────────────────────────────

const FilterBadge: React.FC<{ filter: FilterSummaryItem }> = ({ filter }) => (
  <div className="flex items-center gap-1 text-[10px] bg-muted/80 rounded px-1.5 py-0.5">
    <span className="text-muted-foreground">{filter.field}</span>
    <span className="text-yellow-600 dark:text-yellow-400 font-mono">{filter.operator}</span>
    <span className="text-foreground font-medium">{filter.value}</span>
  </div>
);

const StrategySection: React.FC<{ strategy: StrategySummaryItem }> = ({ strategy }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div
      className="border border-border/60 rounded-md overflow-hidden"
      style={{ borderLeftColor: strategy.color || undefined, borderLeftWidth: strategy.color ? 3 : undefined }}
    >
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-1.5 px-2 py-1 text-left hover:bg-muted/60 transition-colors"
      >
        {expanded ? <ChevronDown className="h-3 w-3 shrink-0" /> : <ChevronRight className="h-3 w-3 shrink-0" />}
        <Zap className="h-3 w-3 text-yellow-500 shrink-0" />
        <span className="text-[11px] font-semibold truncate flex-1">{strategy.name}</span>
        <span className="text-[9px] text-muted-foreground px-1 bg-muted rounded">{strategy.category}</span>
        {!strategy.enabled && (
          <span className="text-[9px] text-red-400 px-1 bg-red-500/10 rounded">OFF</span>
        )}
      </button>

      {expanded && (
        <div className="px-2 pb-2 space-y-1.5 border-t border-border/50 bg-muted/50">
          {/* Description */}
          <p className="text-[10px] text-muted-foreground pt-1 leading-tight">{strategy.description}</p>

          {/* Alert types */}
          {strategy.alertTypes.length > 0 && (
            <div>
              <div className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5">
                Alerts (OR logic)
              </div>
              <div className="flex flex-wrap gap-0.5">
                {strategy.alertTypes.map((a) => (
                  <span key={a} className="text-[9px] bg-blue-500/15 text-blue-600 dark:text-blue-400 rounded px-1 py-0.5 font-mono">
                    {a}
                  </span>
                ))}
              </div>
              <p className="text-[9px] text-muted-foreground mt-0.5 italic">{strategy.alertLogic}</p>
            </div>
          )}

          {/* Filters */}
          {strategy.filters.length > 0 && (
            <div>
              <div className="text-[9px] font-medium text-muted-foreground uppercase tracking-wider mb-0.5">
                Filters (AND logic)
              </div>
              <div className="flex flex-wrap gap-0.5">
                {strategy.filters.map((f, i) => (
                  <FilterBadge key={i} filter={f} />
                ))}
              </div>
              <p className="text-[9px] text-muted-foreground mt-0.5 italic">{strategy.filterLogic}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────

interface StrategySummaryPopoverProps {
  scannerId: string;
  /** Compact mode for ProScanner mini panels (smaller icon, side-aligned popover) */
  compact?: boolean;
  className?: string;
}

const StrategySummaryPopover: React.FC<StrategySummaryPopoverProps> = ({
  scannerId,
  compact = false,
  className = '',
}) => {
  const [open, setOpen] = useState(false);
  const [summary, setSummary] = useState<ScannerPanelSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  // Fetch summary on first open
  const fetchSummary = useCallback(async () => {
    if (summary) return; // already loaded
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch(`${API_BASE}/api/Scanner/${encodeURIComponent(scannerId)}/summary`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data: ScannerPanelSummary = await res.json();
      setSummary(data);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load summary');
    } finally {
      setLoading(false);
    }
  }, [scannerId, summary]);

  const toggle = useCallback(() => {
    const next = !open;
    setOpen(next);
    if (next) fetchSummary();
  }, [open, fetchSummary]);

  // Close on click outside
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (
        popoverRef.current &&
        !popoverRef.current.contains(e.target as Node) &&
        btnRef.current &&
        !btnRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open]);

  const iconSize = compact ? 'h-3 w-3' : 'h-3.5 w-3.5';

  return (
    <div className={`relative inline-flex ${className}`}>
      {/* Trigger button */}
      <button
        ref={btnRef}
        onClick={(e) => { e.stopPropagation(); toggle(); }}
        className={`inline-flex items-center justify-center rounded transition-colors
          ${compact
            ? 'h-4 w-4 hover:bg-white/20'
            : 'h-5 w-5 hover:bg-accent/50'
          }
          ${open ? 'text-blue-500' : 'text-muted-foreground hover:text-foreground'}
        `}
        title="View scanner strategy summary"
      >
        <Info className={iconSize} />
      </button>

      {/* Popover */}
      {open && (
        <div
          ref={popoverRef}
          className="absolute z-50 top-full mt-1 right-0 w-[320px] max-h-[420px] overflow-y-auto
                     bg-popover/95 backdrop-blur-xl border border-border rounded-lg shadow-2xl ring-1 ring-white/5
                     animate-in fade-in-0 zoom-in-95 slide-in-from-top-1"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-3 py-2 border-b border-border/50 bg-muted/80 sticky top-0 z-10">
            <div className="flex items-center gap-1.5 min-w-0">
              <Info className="h-3.5 w-3.5 text-blue-500 shrink-0" />
              <span className="text-[11px] font-bold truncate text-foreground">
                {summary?.name ?? scannerId}
              </span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="h-4 w-4 flex items-center justify-center rounded hover:bg-accent/50 text-muted-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </div>

          <div className="p-2.5 space-y-2.5 text-foreground">
            {loading && (
              <div className="text-[10px] text-muted-foreground text-center py-4">Loading summary…</div>
            )}

            {error && (
              <div className="text-[10px] text-red-400 text-center py-2">Error: {error}</div>
            )}

            {summary && (
              <>
                {/* Description */}
                <p className="text-[11px] text-muted-foreground leading-tight">{summary.description}</p>

                {/* Scanner type & max results */}
                <div className="flex items-center gap-2 text-[10px]">
                  <span className="bg-muted rounded px-1.5 py-0.5 text-muted-foreground">{summary.type}</span>
                  <span className="text-muted-foreground">Max {summary.maxResults} results</span>
                </div>

                {/* Scanner-level filters */}
                {summary.filters.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <Filter className="h-3 w-3 text-orange-500" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Scanner Filters ({summary.filters.length})
                      </span>
                    </div>
                    <div className="space-y-0.5">
                      {summary.filters.map((f, i) => (
                        <div key={i} className="text-[10px] flex items-baseline gap-1 pl-1">
                          <span className="text-muted-foreground w-1 shrink-0">•</span>
                          <span>{f.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sort */}
                {summary.sort && (
                  <div className="flex items-center gap-1 text-[10px]">
                    <ArrowUpDown className="h-3 w-3 text-purple-500" />
                    <span className="font-medium text-muted-foreground">Sort:</span>
                    <span>{summary.sort.field}</span>
                    <span className="text-muted-foreground">({summary.sort.direction})</span>
                  </div>
                )}

                {/* Strategies feeding this panel */}
                {summary.strategies.length > 0 && (
                  <div>
                    <div className="flex items-center gap-1 mb-1">
                      <Zap className="h-3 w-3 text-yellow-500" />
                      <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Strategies ({summary.strategies.length})
                      </span>
                    </div>
                    <div className="space-y-1">
                      {summary.strategies.map((s) => (
                        <StrategySection key={s.id} strategy={s} />
                      ))}
                    </div>
                  </div>
                )}

                {/* No strategies message */}
                {summary.strategies.length === 0 && (
                  <div className="text-[10px] text-muted-foreground italic pt-1">
                    No strategies assigned — results come from scanner filters only.
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default StrategySummaryPopover;
